import type { RhythmShare, RhythmStateName } from '@steado/admin-contract/rhythm';

import type { ToneName } from '@/components/table/tone';

/**
 * Present states read as fine, the three absences as progressively worse.
 * Building and steady share a tone on purpose: the brand accent is a red, and
 * a healthy state must not sit beside "lapsed" looking like one.
 */
export const STATE_TONES: Readonly<Record<RhythmStateName, ToneName>> = {
  new: 'info',
  building: 'ok',
  steady: 'ok',
  returning: 'purple',
  slipping: 'warn',
  lapsed: 'err',
  dormant: 'mute',
};

export const count = (value: number): string => value.toLocaleString('en-IN');

/** Minutes since local midnight as `HH:mm`. */
export const localMinute = (minutes: number): string =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

/**
 * A share as a percentage, or a dash when nobody was in the denominator.
 * "0%" would say everyone failed; nobody having been asked is a different fact.
 */
export function sharePercent(share: RhythmShare): string {
  return share.of === 0 ? '—' : `${String(Math.round((share.count / share.of) * 100))}%`;
}

export function shareCounts(share: RhythmShare): string {
  return share.of === 0 ? 'nobody to count' : `${count(share.count)} of ${count(share.of)}`;
}

/** Events per thousand sends, the unit the guardrails are judged in. */
export function perThousand(events: number, sends: number): string {
  if (sends === 0) return '—';
  const rate = (events / sends) * 1000;
  return rate === 0 ? '0' : rate < 10 ? rate.toFixed(1) : String(Math.round(rate));
}
